import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { SITE } from '../lib/site';
import { AppLink } from './AppLink';
import { Icon } from './Icon';

export const NAV_ITEMS = [
  { href: '/', label: 'Home' },
  { href: '/services', label: 'Services' },
  { href: '/portfolio', label: 'Portfolio' },
  { href: '/about', label: 'About' },
  { href: '/journal', label: 'Journal' },
  { href: '/contact', label: 'Contact' },
] as const;

const isActive = (href: string, location: string) =>
  href === '/' ? location === '/' : location === href || location.startsWith(`${href}/`);

export function Header() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu after navigating, and on Escape.
  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-surface-ivory/85 backdrop-blur-xl shadow-[0_1px_8px_rgba(0,0,0,0.04)]">
      <div className="h-20 max-w-[1440px] mx-auto px-margin-mobile md:px-gutter flex items-center justify-between gap-space-md">
        <AppLink href="/" className="flex items-center gap-space-sm" aria-label={`${SITE.name} — home`}>
          <picture>
            <source type="image/webp" srcSet="/brand/logo-mark.webp" />
            <img src="/brand/logo-mark.png" alt="" width={82} height={82} className="h-9 w-9 object-contain" />
          </picture>
          <span className="font-headline-sm text-headline-sm text-primary tracking-tight">{SITE.shortName}</span>
        </AppLink>

        <nav aria-label="Main" className="hidden xl:flex items-center gap-space-lg">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.href, location);
            return (
              <AppLink
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={`relative font-label-lg text-label-lg uppercase tracking-wider transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-gold-dark after:transition-all after:duration-300 ${
                  active
                    ? 'text-primary font-bold after:w-full'
                    : 'text-on-surface-variant hover:text-primary after:w-0 hover:after:w-full'
                }`}
              >
                {item.label}
              </AppLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-space-md">
          <a
            className="hidden lg:flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-on-surface-variant hover:text-primary transition-colors"
            href={`tel:${SITE.phoneE164}`}
          >
            <Icon name="call" size={16} className="text-secondary" />
            <span>{SITE.phoneDisplay}</span>
          </a>
          <AppLink
            href="/contact"
            className="hidden sm:inline-flex items-center justify-center bg-primary text-gold-light hover:bg-primary-container hover:text-on-primary font-label-lg text-label-lg uppercase tracking-wider px-space-md py-space-sm rounded shadow-[0_4px_20px_rgba(3,37,23,0.18)] transition-all duration-300"
          >
            Get Consultation / Quote
          </AppLink>
          <button
            type="button"
            className="xl:hidden w-10 h-10 rounded-lg flex items-center justify-center text-primary hover:bg-surface-cream transition-colors"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            <Icon name={open ? 'close' : 'menu'} size={26} />
          </button>
        </div>
      </div>

      <div
        id="mobile-menu"
        hidden={!open}
        className="xl:hidden border-t border-gold-light/40 bg-surface-ivory/95 backdrop-blur-xl"
      >
        <nav aria-label="Mobile" className="max-w-[1440px] mx-auto px-margin-mobile md:px-gutter py-space-md flex flex-col">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.href, location);
            return (
              <AppLink
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={`py-3 border-b border-border-muted font-label-lg text-label-lg uppercase tracking-wider ${
                  active ? 'text-primary font-bold' : 'text-on-surface-variant'
                }`}
              >
                {item.label}
              </AppLink>
            );
          })}
          <div className="flex flex-col sm:flex-row gap-space-sm pt-space-md">
            <a
              href={`tel:${SITE.phoneE164}`}
              className="inline-flex items-center justify-center gap-2 border border-gold-dark/50 text-primary font-label-lg text-label-lg uppercase tracking-wider px-space-md py-3 rounded"
            >
              <Icon name="call" size={16} /> {SITE.phoneDisplay}
            </a>
            <AppLink
              href="/contact"
              className="inline-flex items-center justify-center bg-primary text-gold-light font-label-lg text-label-lg uppercase tracking-wider px-space-md py-3 rounded"
            >
              Get Consultation / Quote
            </AppLink>
          </div>
        </nav>
      </div>
    </header>
  );
}
